import React from "react";
import { Card, SectionHeading } from "./RedlandComponents";
import { useSiteContent } from "../context/ContentContext";

const ICON_PATHS = {
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
  sheet: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h8M12 11v8",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  mail: "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6",
};

const makeIcon = (name) =>
  function Icon({ size = 20 }) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={ICON_PATHS[name]} />
      </svg>
    );
  };

const FileText = makeIcon("file");
const FileSpreadsheet = makeIcon("sheet");
const Download = makeIcon("download");
const Eye = makeIcon("eye");
const Mail = makeIcon("mail");

const API = process.env.REACT_APP_BACKEND_URL;

// Uploaded files are served by the API; originals are static site files.
const fileHref = (url, name, download) => {
  if (!url || !url.startsWith("/api/")) return url;
  return `${API}${url}${download ? `?download=${encodeURIComponent(name)}` : ""}`;
};

const action = (label, url, name, type, opts = {}) => ({
  label,
  type,
  href: fileHref(url, name, !opts.view),
  download: opts.view ? undefined : name,
  ...opts,
});

function buildDocs(d) {
  const { information: info, agreement: agr, application: app } = d;
  return [
    {
      id: "information",
      title: "Membership Information",
      fileName: info.fileName,
      fileType: "PDF",
      Icon: FileText,
      description: "Review membership options, pricing, benefits, and club policies.",
      actions: [
        action("View PDF", info.url, info.fileName, "PDF", { view: true }),
        action("Download", info.url, info.fileName, "PDF"),
      ],
    },
    {
      id: "agreement",
      title: "Membership Agreement",
      fileName: agr.fileName,
      fileType: "Word Document (.docx)",
      Icon: FileText,
      description: "Download and complete the membership agreement.",
      actions: [
        action("Download Agreement", agr.url, agr.fileName, "DOCX"),
        ...(agr.pdfUrl ? [action("Printable PDF", agr.pdfUrl, agr.pdfFileName || "Membership Agreement.pdf", "PDF", { secondary: true })] : []),
      ],
    },
    {
      id: "application",
      title: "Membership Application",
      fileName: app.fileName,
      fileType: "Excel Spreadsheet (.xlsx)",
      Icon: FileSpreadsheet,
      description: "Download and complete your membership application.",
      actions: [action("Download Application", app.url, app.fileName, "XLSX")],
    },
  ];
}

function DocAction({ docId, label, href, download, view, type, secondary, index }) {
  const variant = secondary || index > 0 ? "secondary" : "primary";
  const Icon = view ? Eye : Download;
  return (
    <a
      className={`button button-${variant} doc-action`}
      href={href}
      {...(view ? { target: "_blank", rel: "noopener noreferrer" } : { download })}
      aria-label={`${label} (${type}${view ? ", opens in new tab" : ""})`}
      data-testid={`doc-${docId}-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={18} aria-hidden="true" />
      <span>{label}</span>
      <span className="doc-action-type">{type}</span>
    </a>
  );
}

function DocCard({ id, title, fileName, fileType, Icon, description, actions }) {
  return (
    <Card className="doc-card">
      <div className="doc-card-icon" aria-hidden="true">
        <Icon size={26} />
      </div>
      <h3>{title}</h3>
      <p className="doc-card-file" data-testid={`doc-${id}-filename`}>
        {fileName} <span className="doc-type-label">{fileType}</span>
      </p>
      <p>{description}</p>
      <div className="doc-card-actions">
        {actions.map((action, index) => (
          <DocAction key={action.label} docId={id} index={index} {...action} />
        ))}
      </div>
    </Card>
  );
}

export function MembershipDocuments() {
  const { membershipDocuments } = useSiteContent();
  const docs = buildDocs(membershipDocuments);
  return (
    <section id="membership-documents" className="section section-cream membership-documents" data-testid="membership-documents-section">
      <div className="container">
        <SectionHeading
          eyebrow="Apply Today"
          title="Membership Documents"
          copy="Interested in becoming a member? Review our membership information, download the application and agreement, and email your completed documents to us."
        />
        <div className="card-grid three doc-grid">
          {docs.map((doc) => (
            <DocCard key={doc.id} {...doc} />
          ))}
        </div>
        <div className="doc-email-notice" role="note" data-testid="membership-documents-email-notice">
          <Mail size={24} aria-hidden="true" />
          <p>
            E-mail all completed documents to{" "}
            <a href="mailto:redlandgolf@aol.com" data-testid="membership-documents-email-link">
              redlandgolf@aol.com
            </a>
          </p>
        </div>
        <p className="doc-approval-note" data-testid="membership-documents-approval-note">
          Membership applications are subject to approval by the Membership Committee. The club will contact approved applicants to arrange payment.
        </p>
      </div>
    </section>
  );
}
